/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { Button } from "@/components/ui/button";
import { isWaitlistMode, openWaitlist } from "@/lib/utils/waitlist-mode";
import { ArrowRight } from "lucide-react";
import { BrowserChrome } from "../primitives";
import type { SolutionHero } from "../solution-template.types";

interface SolutionHeroSectionProps {
  hero: SolutionHero;
}

export function SolutionHeroSection({ hero }: SolutionHeroSectionProps) {
  return (
    <section
      className="relative bg-gradient-to-br from-primary to-primary/90 grain-texture"
      aria-label="Hero section"
    >
      <div
        className="absolute inset-0 blueprint-grid-gold"
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20 py-16 md:py-24">
        <div className="flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12">
          <div className="max-w-2xl lg:flex-1 animate-hero-fade">
            <span className="section-badge mb-4 !bg-white/10 !border-white/15 !text-white/70">
              ◆ {hero.badge}
            </span>

            <h1 className="text-[clamp(28px,4.5vw,48px)] leading-[1.1] font-bold font-display text-white mb-4 tracking-[-0.02em]">
              {hero.title}{" "}
              <span className="text-secondary">{hero.titleHighlight}</span>
            </h1>

            <p className="text-[clamp(15px,1.3vw,17px)] leading-[1.6] text-white/60 mb-6 max-w-xl">
              {hero.description}
            </p>

            <Button
              size="pill-lg"
              onClick={() => {
                isWaitlistMode()
                  ? openWaitlist()
                  : (window.location.href = "/auth/sign-up");
              }}
              className="bg-secondary hover:bg-secondary/90 text-white font-medium shadow-lg hover:shadow-xl transition-all hover-lift"
            >
              {isWaitlistMode() ? "Join the Waitlist" : hero.ctaText}
              <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
            </Button>
          </div>

          {/* Product mock */}
          <div className="hidden lg:block lg:flex-1 max-w-md">
            <BrowserChrome className="text-[11px] shadow-xl">
              <div className="bg-background">
                <div className="px-3 py-2 border-b border-border/40 flex items-center justify-between">
                  <span className="font-semibold text-foreground text-xs">
                    Estimate Preview
                  </span>
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-secondary/10 text-secondary dark:bg-secondary/20 dark:text-secondary">
                    Draft
                  </span>
                </div>
                <div className="grid grid-cols-[1fr_48px_36px_56px] gap-px px-3 py-1 bg-muted/40 border-b border-border/40 text-[9px] font-medium text-muted-foreground uppercase tracking-wider">
                  <span>Description</span>
                  <span className="text-right">Qty</span>
                  <span className="text-center">Unit</span>
                  <span className="text-right">Total</span>
                </div>
                {[
                  {
                    desc: "Material — Primary",
                    qty: 120,
                    unit: "EA",
                    total: "$4,800",
                  },
                  {
                    desc: "Labor — Install",
                    qty: 24,
                    unit: "HR",
                    total: "$1,680",
                  },
                  {
                    desc: "Equipment Rental",
                    qty: 2,
                    unit: "DAY",
                    total: "$900",
                  },
                  {
                    desc: "Overhead & Profit Margin",
                    qty: 1,
                    unit: "LS",
                    total: "$1,110",
                  },
                ].map((row, i) => (
                  <div
                    key={row.desc}
                    className={`grid grid-cols-[1fr_48px_36px_56px] gap-px px-3 py-1.5 border-b border-border/20 ${i % 2 === 1 ? "bg-muted/15" : ""}`}
                  >
                    <span className="text-foreground truncate">{row.desc}</span>
                    <span className="text-right tabular-nums">{row.qty}</span>
                    <span className="text-center text-muted-foreground">
                      {row.unit}
                    </span>
                    <span className="text-right font-medium tabular-nums">
                      {row.total}
                    </span>
                  </div>
                ))}
                <div className="px-3 py-2 border-t border-border/40 flex justify-between font-semibold text-xs">
                  <span>Total</span>
                  <span className="tabular-nums">$8,490</span>
                </div>
              </div>
            </BrowserChrome>
          </div>
        </div>
      </div>
    </section>
  );
}
